import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../../App';
import { X, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Preview Workspace drawer — shows the first N parsed lines of a file
 * inside an import session, plus the validation findings. If the file
 * is already IMPORTED, the lines come from bank_recon_lines; otherwise
 * the backend re-parses the ingest bytes on the fly.
 */
export default function FilePreviewDrawer({ sessionId, fileIndex, fileMeta, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (sessionId == null || fileIndex == null) return;
    setBusy(true);
    axiosInstance.get(
      `/bank-import/sessions/${sessionId}/files/${fileIndex}/preview`,
      { params: { limit: 50 } },
    )
      .then((r) => setData(r.data))
      .catch(() => toast.error('تَعَذَّر جَلب المُعايَنَة'))
      .finally(() => setBusy(false));
  }, [sessionId, fileIndex]);

  const findings = data?.validation?.findings || [];
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="preview-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-3xl bg-white shadow-2xl h-full flex flex-col" dir="rtl">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
          <div>
            <div className="text-base font-bold text-slate-800">مُعايَنَة الملَف</div>
            <div className="text-xs text-slate-500 font-mono">{fileMeta?.name || '—'}</div>
          </div>
          <button onClick={onClose} data-testid="preview-close"
                  className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-4 h-4" />
          </button>
        </div>

        {busy ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
          </div>
        ) : !data ? (
          <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
            لا تُوجَد بَيانات.
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="rounded-lg border bg-slate-50 p-3 grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-[10px] uppercase text-slate-500">القالِب</div>
                <div className="text-sm font-semibold text-slate-800">{data.template_key || '—'}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">السُّطور</div>
                <div className="text-sm font-semibold text-slate-800 font-mono">{data.lines.length}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">المَصدَر</div>
                <div className="text-sm font-semibold text-slate-800">
                  {data.source === 'statement' ? 'مَخزون' : 'قِراءَة آنية'}
                </div>
              </div>
            </div>

            {findings.length > 0 && (
              <div className="rounded-lg border bg-amber-50/40 border-amber-200 p-3">
                <div className="text-xs font-semibold text-amber-800 mb-2">
                  نَتائِج التَّحَقُّق ({findings.length})
                </div>
                <ul className="space-y-1 text-xs">
                  {findings.map((f, i) => (
                    <li key={i} data-testid={`finding-${i}`} className="flex items-start gap-1.5">
                      {f.severity === 'error' ?
                        <AlertTriangle className="w-3.5 h-3.5 text-red-600 mt-0.5" /> :
                       f.severity === 'warning' ?
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5" /> :
                        <CheckCircle2 className="w-3.5 h-3.5 text-sky-600 mt-0.5" />}
                      <span className="text-slate-700">{f.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="rounded-lg border bg-white overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                  <tr>
                    <th className="text-start px-2 py-1.5">#</th>
                    <th className="text-start px-2 py-1.5">التاريخ</th>
                    <th className="text-start px-2 py-1.5">الوَصف</th>
                    <th className="text-end px-2 py-1.5">المَبلَغ</th>
                    <th className="text-end px-2 py-1.5">الرَّصيد</th>
                    <th className="text-start px-2 py-1.5">المَرجِع</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lines.map((l, i) => (
                    <tr key={i} className="border-t" data-testid={`preview-line-${i}`}>
                      <td className="px-2 py-1 font-mono text-[10px] text-slate-500">{l.line_no}</td>
                      <td className="px-2 py-1 font-mono text-[11px]">{l.date}</td>
                      <td className="px-2 py-1 max-w-[260px] truncate">{l.description || '—'}</td>
                      <td className={`px-2 py-1 text-end font-mono ${l.amount < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        {Number(l.amount || 0).toFixed(2)}
                      </td>
                      <td className="px-2 py-1 text-end font-mono text-slate-600">
                        {l.balance_after != null ? Number(l.balance_after).toFixed(2) : '—'}
                      </td>
                      <td className="px-2 py-1 text-[10px] font-mono text-slate-500">
                        {l.reference || l.sadad_number || l.invoice_number || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
